import React, { useState } from 'react';
import { Property, AppUser, UserRole } from '../../types';
import { XMarkIcon, ExclamationTriangleIcon } from '../Icons';
import { BrochureModal } from './BrochureModal';
import { CopyFallbackModal } from '../CopyFallbackModal';
import { MassShareActionsGrid } from './massShare/MassShareActionsGrid';
import { BrokerCardOption } from './massShare/BrokerCardOption';
import { downloadBulkExcelApi, downloadBulkCsvApi, createBulkGroupLinkApi } from '../../services/bulkApiClient';
import { BulkSelectionPayload } from '../../server/bulk/bulkTypes';

interface MassShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProperties?: Property[];
  selectionPayload?: BulkSelectionPayload;
  selectedCount: number;
  appUser: AppUser | null;
}

export const MassShareModal: React.FC<MassShareModalProps> = ({
  isOpen,
  onClose,
  selectedProperties = [],
  selectionPayload,
  selectedCount,
  appUser,
}) => {
  const [activeSubModal, setActiveSubModal] = useState<'none' | 'brochure'>('none');
  const [copyModalState, setCopyModalState] = useState<{ isOpen: boolean; text: string; title: string } | null>(null);
  const [includeCard, setIncludeCard] = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (!isOpen) return null;

  const isBroker = Boolean(appUser && [UserRole.BROKER, UserRole.BROKER_INTERNO, UserRole.BROKER_PREMIUM].includes(appUser.role as any));
  const userName = isBroker ? appUser?.nombreCompleto || appUser?.displayName || '' : '';
  const userPhone = isBroker ? appUser?.celular || '' : '';

  const effectivePayload: BulkSelectionPayload = {
    ...(selectionPayload || {
      mode: 'EXPLICIT',
      selectedIds: selectedProperties.map((p) => p.id),
    }),
    userRole: appUser?.role,
  };

  const handleCopyGroupLink = async () => {
    try {
      setLoadingAction('link');
      setActionError(null);
      const res = await createBulkGroupLinkApi({
        selection: effectivePayload,
        baseUrl: window.location.origin,
        includeCard,
        brokerName: userName,
        brokerPhone: userPhone,
      });
      setCopyModalState({ isOpen: true, text: res.groupUrl, title: 'Link Grupal para Cliente' });
    } catch (err: any) {
      setActionError(err.message || 'Error generando link grupal');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      setLoadingAction('csv');
      setActionError(null);
      await downloadBulkCsvApi(effectivePayload);
    } catch (err: any) {
      setActionError(err.message || 'Error descargando CSV');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setLoadingAction('excel');
      setActionError(null);
      await downloadBulkExcelApi(effectivePayload);
    } catch (err: any) {
      setActionError(err.message || 'Error descargando Excel');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-900/80 z-[80] flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fade-in-up">
          <header className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">Opciones de Compartir</h2>
              <p className="text-sm text-sky-600 font-bold">{selectedCount} inmuebles seleccionados</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <XMarkIcon className="w-6 h-6 text-gray-400" />
            </button>
          </header>

          {selectedCount > 30 && (
            <div className="mx-8 mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 font-medium">
                Has seleccionado {selectedCount} inmuebles. El Brochure enviará hasta 30 y el Link Grupal hasta 60.
              </p>
            </div>
          )}

          {actionError && <div className="mx-8 mt-3 p-3 bg-red-50 border border-red-300 rounded-xl text-xs text-red-700 font-medium">{actionError}</div>}

          <div className="p-8 space-y-6 bg-gray-50/50">
            {isBroker && userName && <BrokerCardOption userName={userName} includeCard={includeCard} onToggle={setIncludeCard} />}
            <MassShareActionsGrid
              isLoadingAction={loadingAction}
              onSelectBrochure={() => setActiveSubModal('brochure')}
              onCopyGroupLink={handleCopyGroupLink}
              onDownloadCsv={handleDownloadCsv}
              onDownloadExcel={handleDownloadExcel}
            />
          </div>

          <footer className="px-8 py-4 bg-white border-t border-gray-50 flex justify-end">
            <button onClick={onClose} className="px-6 py-2 text-xs font-black text-gray-400 hover:text-gray-800 uppercase tracking-widest">Cerrar</button>
          </footer>
        </div>
      </div>

      {activeSubModal === 'brochure' && (
        <BrochureModal isOpen={true} onClose={() => setActiveSubModal('none')} selectionPayload={effectivePayload} totalSelectedCount={selectedCount} appUser={appUser} />
      )}
      {copyModalState && (
        <CopyFallbackModal isOpen={copyModalState.isOpen} onClose={() => setCopyModalState(null)} textToCopy={copyModalState.text} title={copyModalState.title} />
      )}
    </>
  );
};
